import { inject, Injectable } from "@angular/core";
import { PdfConversionApiService } from "@domain/project/api/pdf-conversion-api.service";
import { fileToArrayBuffer } from "@utils/file-to-array-buffer";
import * as pdfjsLib from "pdfjs-dist";
import { PageViewport, PDFPageProxy } from "pdfjs-dist";
import { firstValueFrom, forkJoin, from, map, Observable, of, switchMap } from "rxjs";

export enum PdfContentType {
  RASTER = "raster",
  VECTOR = "vector",
  MIXED = "mixed",
  EMPTY = "empty",
}

@Injectable({
  providedIn: "root",
})
export class PdfToImageService {
  private readonly A4_WIDTH_PT: number = 595.28 as const; // 210 mm
  private readonly A4_HEIGHT_PT: number = 841.89 as const; // 297 mm
  private readonly LARGE_PAGE_THRESHOLD: number = 1000 as const;
  private readonly HIGH_RES_SCALE: number = 5 as const;
  private readonly PIXEL_DEVICE_RATIO: number = window.devicePixelRatio || (1 as const);
  private readonly pdfConversionApiService: PdfConversionApiService = inject(PdfConversionApiService);

  analyzePdfPageContent(file: File): Observable<PdfContentType> {
    return from(this.fileToPdf(file)).pipe(
      switchMap((pdf) => {
        if (!pdf) {
          // return a consistent object with same shape as forkJoin result
          return of({
            opList: { fnArray: [] } as any,
            textContent: { items: [] } as any,
          });
        }
        return from(pdf.getPage(1)).pipe(
          switchMap((page) =>
            forkJoin({
              opList: from(page.getOperatorList()),
              textContent: from(page.getTextContent()),
            }),
          ),
        );
      }),

      map(({ opList, textContent }) => {
        const rasterOps = new Set([pdfjsLib.OPS.paintImageXObject, pdfjsLib.OPS.paintInlineImageXObject]);

        const vectorOps = new Set([
          pdfjsLib.OPS.constructPath,
          pdfjsLib.OPS.stroke,
          pdfjsLib.OPS.fill,
          pdfjsLib.OPS.fillStroke,
          pdfjsLib.OPS.setLineWidth,
        ]);

        const hasRasterImage = opList.fnArray.some((op: number) => rasterOps.has(op));
        const hasVectorDrawing = opList.fnArray.some((op: number) => vectorOps.has(op));
        const hasVectorText = textContent.items.length > 0;

        if (hasRasterImage && (hasVectorDrawing || hasVectorText)) return PdfContentType.MIXED;
        if (hasRasterImage) return PdfContentType.RASTER;
        if (hasVectorDrawing || hasVectorText) return PdfContentType.VECTOR;
        return PdfContentType.EMPTY;
      }),
    );
  }

  convertToPng(file: any): Observable<string> {
    return this.pdfConversionApiService.convertToPng(file);
  }

  async convert(file: File, viewport: any) {
    const pdf = await this.fileToPdf(file);
    if (pdf) {
      const page = await pdf.getPage(1);
      const scale = await firstValueFrom(this.calculateScale(file, page));

      viewport = page.getViewport({ scale: scale });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.style.width = `${viewport.width / this.PIXEL_DEVICE_RATIO}px`;
      canvas.style.height = `${viewport.height / this.PIXEL_DEVICE_RATIO}px`;
      const renderContext = {
        canvasContext: context as CanvasRenderingContext2D,
        viewport: viewport,
        canvas: canvas,
      };

      const { promise } = page.render(renderContext);
      await promise;
      return canvas as HTMLCanvasElement;
    }
    return undefined;
  }

  private async fileToPdf(file: File): Promise<pdfjsLib.PDFDocumentProxy | undefined> {
    const buffer = await fileToArrayBuffer(file);
    pdfjsLib.GlobalWorkerOptions.workerSrc = "/assets/pdf.worker.min.mjs";
    const { promise: loadingTask } = pdfjsLib.getDocument({
      data: buffer as string,
    });

    try {
      return await loadingTask;
    } catch (e: any) {
      console.error("Invalid PDF", "The uploaded PDF could not be processed.", e);
      return undefined;
    }
  }

  private calculateScale(file: File, page: PDFPageProxy): Observable<number> {
    const originalViewPort: PageViewport = page.getViewport({ scale: 1 });
    const originalWidth = originalViewPort.width;
    const originalHeight = originalViewPort.height;
    const scaleX = this.A4_WIDTH_PT / originalWidth;
    const scaleY = this.A4_HEIGHT_PT / originalHeight;

    if (originalWidth > this.LARGE_PAGE_THRESHOLD || originalHeight > this.LARGE_PAGE_THRESHOLD) return of(1);

    return this.analyzePdfPageContent(file).pipe(
      map((pdfContentType) => {
        if (pdfContentType === PdfContentType.VECTOR || pdfContentType === PdfContentType.MIXED) {
          return Math.min(scaleX, scaleY) * this.PIXEL_DEVICE_RATIO * this.HIGH_RES_SCALE;
        }
        return this.PIXEL_DEVICE_RATIO;
      }),
    );
  }
}
